/**
 * Run output triggers against plain mud text (ANSI stripped).
 */

import type { ClientPrefs, Trigger } from "./prefs";
import { activeTriggers } from "./prefs";
import { playBeep } from "./sound";

export interface TriggerFire {
  command?: string;
  sound: boolean;
  pattern: string;
}

const COOLDOWN_MS = 750;
const MAX_FIRES_PER_CHUNK = 8;

const lastFire = new Map<string, number>();

function compileMatcher(
  pattern: string
): { test: (s: string) => boolean; expand: (s: string, cmd: string) => string } | null {
  // /regex/flags
  if (pattern.startsWith("/") && pattern.length >= 3) {
    const last = pattern.lastIndexOf("/");
    if (last > 0) {
      const body = pattern.slice(1, last);
      const flags = pattern.slice(last + 1) || "i";
      try {
        const re = new RegExp(body, flags.includes("g") ? flags : flags);
        return {
          test: (s) => {
            re.lastIndex = 0;
            return re.test(s);
          },
          expand: (s, cmd) => {
            re.lastIndex = 0;
            const m = re.exec(s);
            if (!m) return cmd;
            return cmd.replace(/\$(\d)/g, (_, n) => m[Number(n)] ?? "");
          },
        };
      } catch {
        return null;
      }
    }
  }

  // literal substring, case-insensitive
  const lit = pattern.toLowerCase();
  return {
    test: (s) => s.toLowerCase().includes(lit),
    expand: (_s, cmd) => cmd,
  };
}

/**
 * Evaluate triggers against a plain-text chunk of mud output.
 * Returns commands to send (in order) and whether any beep fired.
 */
export function evaluateTriggers(
  plainChunk: string,
  prefs: ClientPrefs,
  opts?: { suppressCommands?: boolean }
): TriggerFire[] {
  if (!plainChunk) return [];

  const fires: TriggerFire[] = [];
  const now = Date.now();
  const list = activeTriggers(prefs).filter((t) => t.enabled);

  for (const t of list) {
    if (fires.length >= MAX_FIRES_PER_CHUNK) break;

    const key = `${t.pattern}\0${t.command}\0${t.sound}`;
    const last = lastFire.get(key) ?? 0;
    if (now - last < COOLDOWN_MS) continue;

    const m = compileMatcher(t.pattern);
    if (!m || !m.test(plainChunk)) continue;

    lastFire.set(key, now);

    const fire: TriggerFire = {
      sound: t.sound,
      pattern: t.pattern,
    };

    if (t.command && !opts?.suppressCommands) {
      fire.command = m.expand(plainChunk, t.command);
    }

    if (fire.sound || fire.command) {
      fires.push(fire);
    }
  }

  return fires;
}

export function applyTriggerSounds(fires: TriggerFire[]): void {
  if (fires.some((f) => f.sound)) {
    playBeep();
  }
}

/** For UI testing / docs. */
export function describeTrigger(t: Trigger): string {
  const parts = [t.enabled ? "on" : "off", t.pattern];
  if (t.command) parts.push(`→ ${t.command}`);
  if (t.sound) parts.push("beep");
  return parts.join(" ");
}
